import { useEffect, useRef, useState } from 'react';
import {
  GUT_GENUG_METER, OPTIONEN, alsMessung, besser, gutGenug, ortungsfehler,
  type Messung,
} from './standort.ts';

/**
 * DIE KAMERA IN EINEM FENSTER, DAS UNS GEHÖRT.
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (18.09.2026)
 *   „foto machen klappt noch immer nicht. das handy nimmt bei der aufnahme
 *    des bildes doch gps daten. oder du musst, wie das apps machen, die
 *    amera öffnen aber in einem fenster von uns, wo du dann die ganzen
 *    georeferenzierten daten direkt einblendest, gps gestartet wird und du
 *    dann das foto machst. dann brauchst du nicht im nachhinein GPS suchen
 *    lassen, so wie es jetzt ist funktioniert es nicht."
 *
 * ----------------------------------------------------------------------------
 * ER HAT DEN FEHLER UND DIE BEHEBUNG IN EINEM SATZ GENANNT
 * ----------------------------------------------------------------------------
 *
 * Der Weg über `<input type="file" capture>` ruft die KAMERA-APP DES
 * BETRIEBSSYSTEMS. In dem Augenblick liegt unsere Seite im Hintergrund, und
 * dort halten iOS und Android die Rückrufe von `watchPosition` an. Gemessen
 * wird also erst, wenn die App zurückkommt - und dann steht der Monteur mit
 * dem fertigen Foto da und wartet auf einen kalten GPS-Fix von 5 bis 20
 * Sekunden. Die .166 hat die Wartezeit von 4 auf 15 Sekunden gestellt; das
 * hat den Verlust des Fotos behoben und nicht das Warten. **Eine Wartezeit,
 * die man verlängert, bleibt eine Wartezeit.**
 *
 * Zu „das handy nimmt bei der aufnahme des bildes doch gps daten": ja, in
 * die EXIF-Daten der Datei - aber nur, wenn die Ortung für die Kamera-App
 * erlaubt ist, und dann liest sie ein Browser trotzdem nicht: `capture`
 * liefert auf iOS eine neu kodierte Datei, aus der die Kamera-App die
 * Ortsangabe entfernt hat. Ein Weg, der von einer Einstellung in einer
 * FREMDEN App abhängt, ist kein Weg.
 *
 * ----------------------------------------------------------------------------
 * WAS DIESES FENSTER ANDERS MACHT
 * ----------------------------------------------------------------------------
 *
 * Die Seite bleibt im VORDERGRUND. Damit läuft die Messung, während der
 * Monteur das Bild ausrichtet, und in dieser Zeit stellt das Gerät von der
 * Funkzelle auf die Satelliten um. Beim Auslösen steht die Zahl schon da:
 *
 *   1. Tipp auf „Foto aufnehmen". Das Fenster geht auf, `getUserMedia` holt
 *      das Bild der rückwärtigen Kamera, `watchPosition` läuft los.
 *   2. Über dem Bild steht, was gemessen ist: Koordinaten, Genauigkeit, Zeit,
 *      Höhe, Blickrichtung. **Der Monteur sieht vor dem Auslösen, was auf das
 *      Bild kommt** - und wenn er 300 Meter liest, geht er 3 Schritte aus dem
 *      Hausflur heraus.
 *   3. Auslösen zeichnet das laufende Bild auf eine Leinwand und macht daraus
 *      eine JPEG-Datei. Kein Wechsel in eine andere App, kein Hintergrund,
 *      keine angehaltene Wache.
 *   4. Die Datei geht mit der Messung dieses Augenblicks hinaus.
 *
 * DER STEMPEL WIRD WEITERHIN AUF DEM SERVER GEBRANNT (`regeln/geostempel.ts`).
 * Was hier eingeblendet wird, ist die VORSCHAU darauf. Zwei Stellen, die
 * denselben Text auf dasselbe Bild schreiben, wären 2 Wahrheiten - und die
 * auf dem Server ist die, die niemand übergehen kann.
 *
 * ----------------------------------------------------------------------------
 * UND ES IST DER ZWEITE WEG UND NICHT DER EINZIGE
 * ----------------------------------------------------------------------------
 *
 * `getUserMedia` gibt es nicht überall: ein Browser über `http://` verweigert
 * es, ein Rechner ohne Kamera hat nichts zu geben, und mancher Firmenbrowser
 * schaltet es ab. Dann bleibt der alte Weg über das Dateifeld - er
 * funktioniert, er ist nur langsamer. **Ein neuer Weg, der den alten
 * mitnimmt, ist kein Fortschritt.**
 */

/** Was das Fenster liefert. */
export interface Aufnahme {
  datei: File;
  /** `null` heisst: es kam keine Messung. Der Aufrufer entscheidet dann. */
  messung: Messung | null;
}

/** Gibt es in diesem Browser überhaupt ein Kamerabild für uns? */
export function kamerafensterGeht(): boolean {
  return typeof navigator !== 'undefined'
    && !!navigator.mediaDevices
    && typeof navigator.mediaDevices.getUserMedia === 'function'
    && (typeof window === 'undefined' || window.isSecureContext !== false);
}

/**
 * DIE KAMERA WIRD GEBETEN, NICHT VERMESSEN.
 *
 * `facingMode: { ideal: 'environment' }` ist ein WUNSCH. Als `exact` wäre es
 * ein Riegel, und ein Laptop ohne rückwärtige Kamera bekäme gar kein Bild -
 * obwohl seine eine Kamera genau die ist, die gemeint war.
 *
 * DIE AUFLÖSUNG EBENSO. `ideal: 2560` heisst „so viel wie du hast, bis
 * hierher"; eine feste Zahl lehnt ein Gerät ab, das sie nicht kann. Ein
 * Nachweisfoto muss eine Hausnummer und einen Rohrverband lesbar zeigen -
 * darunter ist es kein Nachweis.
 */
const KAMERAWUNSCH: MediaStreamConstraints = {
  audio: false,
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 2560 },
    height: { ideal: 1440 },
  },
};

/** Die Güte der JPEG-Datei. 0,92 ist scharf genug und bleibt unter 1 MB. */
const GUETE = 0.92;

export default function Kamerafenster({ ordner, zu, fertig, rueckfall }: {
  /** Steht in der Überschrift: wer auslöst, soll den Ordner sehen. */
  ordner: string;
  zu: () => void;
  fertig: (a: Aufnahme) => void;
  /**
   * DER WEG ÜBER DIE KAMERA-APP DES TELEFONS.
   *
   * Er steht NUR da, wenn hier etwas schiefgegangen ist - und dann steht er
   * als KNOPF da und nicht als Satz. **Ein Satz, der auf einen Knopf
   * verweist, den es nicht gibt, ist schlimmer als kein Satz:** er schickt
   * jemanden suchen, der vor einer Grube steht.
   */
  rueckfall?: () => void;
}) {
  const bild = useRef<HTMLVideoElement>(null);
  const strom = useRef<MediaStream | null>(null);
  const wache = useRef<number | null>(null);
  const beste = useRef<Messung | null>(null);

  const [laeuft, setLaeuft] = useState(false);
  const [anzeige, setAnzeige] = useState<Messung | null>(null);
  const [ortfehler, setOrtfehler] = useState<string | null>(null);
  const [bildfehler, setBildfehler] = useState<string | null>(null);
  const [nimmt, setNimmt] = useState(false);
  /* WAS DER BROWSER ZUR ORTUNG SAGT, BEVOR JEMAND RÄT.
     ORIGINALTEXT MARVIN (18.09.2026): „die dienste sind an, der standort wird
     nicht übertragen." Ortungsdienste am Gerät und die Erlaubnis für DIESE
     Seite sind 2 verschiedene Schalter, und der zweite ist der, der hier
     zumacht. `navigator.permissions` sagt es im Klartext - **eine Meldung,
     die den Zustand nur vermutet, schickt jemanden in die falsche
     Einstellung.** Safari kennt die Abfrage erst seit 16; steht sie nicht zur
     Verfügung, bleibt das Feld leer und es wird nichts behauptet. */
  const [erlaubnis, setErlaubnis] = useState<string | null>(null);
  /* DIE MELDUNGEN LIEGEN ZUSAMMENGEKLAPPT ÜBER DEM BILD.
     ORIGINALTEXT MARVIN (18.09.2026): „viel zu viel text und viel zu kleines
     bild." Er hat recht: 2 Kästen mit 8 Zeilen haben das Kamerabild auf ein
     Drittel des Bildschirms gedrückt - **auf einem Nachweisfoto entscheidet
     sich beim Ausrichten, ob es eines wird.** Was zu sagen ist, steht jetzt
     in EINER Zeile; der ganze Satz kommt auf Tippen. */
  const [meldungOffen, setMeldungOffen] = useState(false);
  /* DER ZOOM.
     ORIGINALTEXT MARVIN (18.09.2026): „das foto lässt sich nicht zoomen, er
     zoomt im browser komplett." Ohne `touch-action: none` gehört das
     Aufziehen dem Browser, und der vergrössert die SEITE - Knöpfe, Text und
     Bild. Gemeint ist das BILD. Der Wert gilt für die Anzeige UND für den
     Ausschnitt beim Auslösen: was auf dem Schirm steht, ist das Foto. */
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  zoomRef.current = zoom;
  /** Die Finger, die gerade auf dem Bild liegen. */
  const zeiger = useRef(new Map<number, { x: number; y: number }>());
  const start = useRef<{ abstand: number; zoom: number } | null>(null);

  /** Der Abstand der 2 Finger, in Bildpunkten. */
  function abstandJetzt(): number {
    const f = [...zeiger.current.values()];
    if (f.length < 2) return 0;
    return Math.hypot(f[0]!.x - f[1]!.x, f[0]!.y - f[1]!.y);
  }

  /* DER ZOOM HAT EINE OBERE UND EINE UNTERE KANTE.
     Unter 1 gäbe es schwarze Ränder; über 4 zeigt das Bild Bildpunkte statt
     Hausnummern. **Ein Zoom ohne Deckel verspricht eine Schärfe, die die
     Linse nicht hat.** */
  function stelle(z: number) {
    setZoom(Math.min(4, Math.max(1, z)));
  }

  /* DAS BILD UND DIE MESSUNG STARTEN ZUSAMMEN, UND ZWAR BEIM AUFGEHEN.
     Nicht auf Knopfdruck: der Knopf, der dieses Fenster geöffnet hat, WAR die
     Handlung des Menschen - iOS verlangt eine solche für die Rückfrage nach
     dem Standort, und sie gilt für diesen Durchlauf. Wer hier noch einmal auf
     einen Knopf wartete, verschenkte genau die Sekunden, um derentwillen
     dieses Fenster gebaut ist. */
  useEffect(() => {
    let gilt = true;

    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia(KAMERAWUNSCH);
        if (!gilt) { s.getTracks().forEach((t) => t.stop()); return; }
        strom.current = s;
        if (bild.current) {
          bild.current.srcObject = s;
          /* `play()` WIRD ABGEWARTET UND SEIN FEHLER GESCHLUCKT. Manche
             Browser lehnen ihn ab, obwohl das Bild danach läuft
             (`autoPlay` hat es schon gestartet); eine Ausnahme hier liesse
             das Fenster leer stehen. */
          try { await bild.current.play(); } catch { /* läuft trotzdem */ }
        }
        if (gilt) setLaeuft(true);
      } catch (f: any) {
        if (!gilt) return;
        /* DER GRUND STEHT DA, UND ER NENNT DEN WEG.
           `NotAllowedError` heisst: der Mensch oder eine Einstellung hat
           abgelehnt. `NotFoundError`: es gibt keine Kamera. Beides braucht
           einen anderen Satz - „Kamera nicht verfügbar" sagt niemandem, was
           zu tun ist. */
        const art = String(f?.name ?? '');
        setBildfehler(
          art === 'NotAllowedError'
            ? 'Dieser Browser darf die Kamera nicht benutzen. In den '
              + 'Einstellungen der Seite den Kamerazugriff erlauben und den '
              + 'Knopf noch einmal tippen.'
            : art === 'NotFoundError' || art === 'OverconstrainedError'
              ? 'Dieses Gerät hat keine Kamera, die der Browser benutzen '
                + 'darf.'
              : 'Die Kamera ließ sich nicht öffnen: '
                + String(f?.message ?? f));
      }
    })();

    /* WAS DER BROWSER ÜBER DIE ERLAUBNIS SAGT. Nur gefragt, nie geraten. */
    try {
      (navigator as any).permissions?.query?.({ name: 'geolocation' })
        .then((r: any) => { if (gilt) setErlaubnis(String(r?.state ?? '')); })
        .catch(() => { /* Safari vor 16 kennt die Frage nicht */ });
    } catch { /* ebenso */ }

    /* UND DIE MESSUNG, IM SELBEN AUGENBLICK. Sie hängt NICHT am Kamerabild:
       eine Kamera, die 2 Sekunden zum Aufwachen braucht, darf die Messung
       nicht um 2 Sekunden verzögern. */
    if (!navigator.geolocation) {
      setOrtfehler(ortungsfehler(undefined));
    } else {
      wache.current = navigator.geolocation.watchPosition(
        (p) => {
          const m = alsMessung(p as any);
          if (!m || !gilt) return;
          beste.current = besser(beste.current, m);
          setAnzeige(beste.current);
          setOrtfehler(null);
        },
        (f) => {
          /* EIN FEHLER BEENDET DIE WACHE NICHT. `watchPosition` meldet
             zwischendurch „position unavailable" und findet danach doch noch
             etwas. Gemeldet wird er nur, solange NICHTS dasteht. */
          if (gilt && !beste.current) setOrtfehler(ortungsfehler(f?.code));
        },
        OPTIONEN,
      );
    }

    return () => {
      gilt = false;
      /* BEIDES WIRD ABGERÄUMT, UND ZWAR IMMER.
         Ein laufender Kamerastrom hält auf dem Telefon die Linse belegt und
         zeigt in der Statusleiste den grünen Punkt - wer das Fenster
         zumacht und den Punkt weiter sieht, glaubt uns nichts mehr. Eine
         laufende Wache hält das GPS an und leert den Akku, und der Monteur
         merkt es erst am Nachmittag. */
      strom.current?.getTracks().forEach((t) => t.stop());
      strom.current = null;
      if (wache.current !== null) {
        navigator.geolocation?.clearWatch(wache.current);
        wache.current = null;
      }
    };
  }, []);

  /**
   * AUSLÖSEN: DAS LAUFENDE BILD WIRD ZUR DATEI.
   *
   * `videoWidth` und nicht die Breite auf dem Bildschirm: das Bild ist auf
   * einem Telefon rund 390 Punkte breit und der Strom 2560 - wer die
   * Bildschirmbreite nimmt, legt ein Nachweisfoto in Briefmarkengröße ab.
   */
  async function ausloesen() {
    const v = bild.current;
    if (!v || nimmt) return;
    const b = v.videoWidth;
    const h = v.videoHeight;
    if (!b || !h) {
      setBildfehler('Das Kamerabild ist noch nicht da. Einen Augenblick '
                    + 'warten und noch einmal tippen.');
      return;
    }
    setNimmt(true);
    try {
      /* DER ZOOM SCHNEIDET DAS BILD, ER VERGRÖSSERT ES NICHT.
         Was auf dem Schirm steht, IST das Foto: bei 2-fach liegt der mittlere
         halbe Bildbereich an, also wird genau der genommen. Die Leinwand
         behält die Größe des Ausschnitts - **ein hochgerechnetes Bild sieht
         schärfer aus, als es ist**, und ein Nachweisfoto, das mehr verspricht
         als es zeigt, ist schlimmer als ein kleines. */
      const z = Math.max(1, zoomRef.current);
      const sb = Math.round(b / z);
      const sh = Math.round(h / z);
      const sx = Math.round((b - sb) / 2);
      const sy = Math.round((h - sh) / 2);

      const leinwand = document.createElement('canvas');
      leinwand.width = sb;
      leinwand.height = sh;
      const stift = leinwand.getContext('2d');
      if (!stift) throw new Error('Diese Ansicht kann kein Bild zeichnen.');
      stift.drawImage(v, sx, sy, sb, sh, 0, 0, sb, sh);

      /* `toBlob` UND NICHT `toDataURL`. Die Zeichenkette einer 2560er
         Aufnahme ist rund 3 MB Text im Arbeitsspeicher, und auf einem
         älteren Telefon bricht die Seite daran ab. */
      const klumpen: Blob = await new Promise((ja, nein) => {
        leinwand.toBlob(
          (k) => (k ? ja(k) : nein(new Error('Das Bild ließ sich nicht '
                                             + 'in eine Datei umwandeln.'))),
          'image/jpeg', GUETE);
      });

      /* DER NAME TRÄGT DEN ZEITPUNKT. Zwei Fotos aus derselben Minute in
         denselben Ordner dürfen sich nicht überschreiben, und ein Name wie
         „image.jpg" sagt in der Nachweismappe nichts. */
      const jetzt = new Date();
      const p = (n: number) => String(n).padStart(2, '0');
      const name = `Foto_${jetzt.getFullYear()}-${p(jetzt.getMonth() + 1)}-`
        + `${p(jetzt.getDate())}_${p(jetzt.getHours())}${p(jetzt.getMinutes())}`
        + `${p(jetzt.getSeconds())}.jpg`;

      fertig({
        datei: new File([klumpen], name, { type: 'image/jpeg' }),
        messung: beste.current,
      });
    } catch (f: any) {
      setBildfehler(String(f?.message ?? f));
    } finally {
      setNimmt(false);
    }
  }

  const m = anzeige;
  const reif = gutGenug(m);

  return (
    <div className="kamerafenster" role="dialog" aria-modal="true"
         aria-label={`Foto aufnehmen für ${ordner}`}>
      <div className="kamerakopf">
        <div className="kameraziel">
          <b>Foto aufnehmen</b>
          <span className="klein">{ordner}</span>
        </div>
        <button type="button" className="knopf" onClick={zu}>schließen</button>
      </div>

      <div className="kamerabuehne"
           /* DAS AUFZIEHEN GEHÖRT UNS UND NICHT DEM BROWSER.
              `touch-action: none` steht im Stil an `.kamerabuehne`; ohne das
              vergrössert Safari die SEITE, und genau das hat Marvin gesehen:
              „das foto lässt sich nicht zoomen, er zoomt im browser
              komplett." Gerechnet wird der Abstand der 2 Finger gegen den
              Abstand beim Aufsetzen - ein Verhältnis und keine Differenz,
              sonst zoomt ein grosser Bildschirm anders als ein kleiner. */
           onPointerDown={(e) => {
             zeiger.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
             if (zeiger.current.size === 2) {
               start.current = { abstand: abstandJetzt(), zoom: zoomRef.current };
             }
           }}
           onPointerMove={(e) => {
             if (!zeiger.current.has(e.pointerId)) return;
             zeiger.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
             if (zeiger.current.size === 2 && start.current) {
               const jetzt = abstandJetzt();
               if (start.current.abstand > 0) {
                 stelle(start.current.zoom * (jetzt / start.current.abstand));
               }
             }
           }}
           onPointerUp={(e) => {
             zeiger.current.delete(e.pointerId);
             if (zeiger.current.size < 2) start.current = null;
           }}
           onPointerCancel={(e) => {
             zeiger.current.delete(e.pointerId);
             if (zeiger.current.size < 2) start.current = null;
           }}
           /* AM SCHREIBTISCH MACHT DAS RAD MIT STRG DASSELBE - so zoomt jeder
              Betrachter, und es ist der einzige Weg, bei dem das Rollen der
              Seite daneben weiter geht. Dieselbe Regel wie in der
              PDF-Fläche. */
           onWheel={(e) => {
             if (!e.ctrlKey && !e.metaKey) return;
             stelle(zoomRef.current * (e.deltaY < 0 ? 1.1 : 1 / 1.1));
           }}>
        {/* `playsInline` IST DIE ZEILE, OHNE DIE ES AUF IOS NICHT GEHT: ohne
            sie schaltet Safari das Bild in den Vollbildspieler des
            Betriebssystems, und dann ist unsere Seite wieder im Hintergrund -
            genau der Zustand, den dieses Fenster vermeidet.
            `muted` gehört dazu, weil ein Browser ein Bild mit Ton nicht von
            selbst starten lässt. */}
        {/* DAS BILD TRÄGT DEN ZOOM ALS `scale`. Der Ausschnitt beim Auslösen
            rechnet mit derselben Zahl - was auf dem Schirm steht, ist das
            Foto. */}
        <video ref={bild} className="kamerabild" playsInline muted autoPlay
               style={{ transform: `scale(${zoom})` }} />

        {!laeuft && !bildfehler && (
          <div className="kamerawartet">Die Kamera geht auf</div>
        )}

        {/* DIE GEODATEN LIEGEN AUF DEM BILD.
            ORIGINALTEXT MARVIN: „wo du dann die ganzen georeferenzierten
            daten direkt einblendest". Sie liegen DARAUF und nicht daneben:
            wer ausrichtet, sieht auf das Bild, und eine Zahl am Seitenrand
            sieht er nicht. Es ist die Vorschau auf den Stempel, den der
            Server brennt - deshalb dieselbe Reihenfolge und dieselben
            Einheiten. */}
        <div className={'geoschild' + (reif ? ' gut' : '')}>
          {m ? (
            <>
              <div className="geozeile">
                <b>{m.lat.toFixed(6)}, {m.lon.toFixed(6)}</b>
              </div>
              <div className="geozeile">
                ± {Math.round(m.genauigkeit)} m
                {reif ? '' : ` - unter ${GUT_GENUG_METER} m ist gut`}
              </div>
              <div className="geozeile">
                {new Date(m.zeit).toLocaleString('de-DE')}
              </div>
              {m.hoehe !== null && (
                <div className="geozeile">Höhe {Math.round(m.hoehe)} m</div>
              )}
              {m.richtung !== null && (
                <div className="geozeile">
                  Blick {Math.round(m.richtung)} Grad
                </div>
              )}
            </>
          ) : (
            <div className="geozeile">
              {ortfehler ? 'Kein Standort' : 'Der Standort wird gesucht'}
            </div>
          )}
        </div>
      </div>

      {/* ============================================================
          EINE ZEILE STATT ZWEIER KAESTEN
          ============================================================
          ORIGINALTEXT MARVIN (18.09.2026)
            „viel zu viel text und viel zu kleines bild."

          ER HAT RECHT GEHABT. Hier standen 2 Hinweiskästen mit zusammen 8
          Zeilen, und sie haben das Kamerabild auf ein Drittel des
          Bildschirms gedrückt. **Auf einem Nachweisfoto entscheidet sich
          beim Ausrichten, ob es eines wird** - der Platz gehört dem Bild.

          Was zu sagen ist, steht jetzt in EINER Zeile unter dem Bild. Der
          ganze Satz kommt auf Tippen, und er kommt nur, wenn ihn jemand
          haben will. Eine Meldung, die man wegtippen kann, ist keine
          verschwiegene: sie steht da, sie nimmt nur nicht den Bildschirm. */}
      {(bildfehler || (ortfehler && !anzeige)) && (
        <div className="kameramelder">
          <button type="button" className="kamerameldezeile"
                  aria-expanded={meldungOffen}
                  onClick={() => setMeldungOffen(!meldungOffen)}>
            <b>{bildfehler ? 'Kamera' : 'Kein Standort'}</b>
            <span className="klein">
              {meldungOffen ? 'weniger' : 'warum?'}
            </span>
          </button>
          {meldungOffen && (
            <div className="kameramelddetail">
              {bildfehler ?? ortfehler}
              {/* WAS DER BROWSER SELBST SAGT, STEHT DABEI.
                  ORIGINALTEXT MARVIN: „die dienste sind an, der standort
                  wird nicht übertragen." **Die Ortungsdienste des Geräts und
                  die Erlaubnis für DIESE Seite sind 2 Schalter**, und der
                  zweite ist der, der hier zumacht. Wer das nicht weiss,
                  sucht in der falschen Einstellung. */}
              {!bildfehler && erlaubnis === 'denied' && (
                <div className="klein" style={{ marginTop: 6 }}>
                  Der Browser meldet: für diese Seite ist der Standort
                  <b> abgelehnt</b>. Am Gerät nützt es nichts, die
                  Ortungsdienste einzuschalten - die Ablehnung steht bei der
                  Seite. Auf dem iPhone: Einstellungen, Apps, Safari,
                  Standort auf „Fragen" oder „Erlauben", danach die Seite neu
                  laden. Im Safari selbst: das Zeichen links in der
                  Adresszeile, „Einstellungen für diese Website".
                </div>
              )}
              {!bildfehler && erlaubnis === 'prompt' && (
                <div className="klein" style={{ marginTop: 6 }}>
                  Der Browser meldet: er würde fragen. Kommt keine Rückfrage,
                  ist die Seite über eine unverschlüsselte Adresse geöffnet
                  oder die Ortung am Gerät ganz aus.
                </div>
              )}
              {bildfehler && rueckfall && (
                <div className="reihe" style={{ marginTop: 8 }}>
                  <button type="button" className="knopf haupt"
                          onClick={rueckfall}>
                    Kamera des Telefons benutzen
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="kameraleiste">
        {/* DER AUSLÖSER IST EIN DAUMENZIEL UND STEHT UNTEN.
            Wer mit einer Hand hält und mit dem Daumen tippt, erreicht die
            unteren 2 Drittel des Bildschirms. 64 Punkte, weil ein Handschuh
            an der Grube nicht auf 44 trifft. */}
        <button type="button" className="ausloeser" onClick={ausloesen}
                disabled={!laeuft || nimmt}
                aria-label="Auslösen">
          <span className="ausloeserring" />
        </button>
        {/* EIN SATZ, NICHT VIER. Er sagt, was jetzt gilt, und sonst nichts -
            „viel zu viel text" war Marvins Wort, und es galt auch hier. */}
        <div className="klein leise kamerasatz">
          {nimmt ? 'Wird gesichert'
            : reif ? `Standort ± ${Math.round(m!.genauigkeit)} m`
            : anzeige ? `± ${Math.round(m!.genauigkeit)} m, wird genauer`
            : ortfehler ? 'Ohne Standort - Auslösen geht'
            : 'Standort wird gesucht'}
          {zoom > 1 && `, ${zoom.toFixed(1)}-fach`}
        </div>
      </div>
    </div>
  );
}
